import { createAppKit, getAppKit, type AppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { mainnet, arbitrum, base, polygon, bsc, avalanche } from 'viem/chains';
import { http } from 'viem';
import { getRpcUrl } from '../utils/validation';

const DEFAULT_PROJECT_ID = 'b2990e32b1456badac6f51e45feef1ff';
const PLACEHOLDER_IDS = new Set([
  'your_project_id_here',
  'your_wallet_connect_project_id_here',
  // Old staging project ID that should no longer be used
  '407d012698988e1aaf18801cd379a8b8'
]);

function readRuntimeProjectId(): string | undefined {
  // Prefer build-time environment variables (e.g., Cloudflare secrets)
  const envValue = import.meta.env?.VITE_WALLETCONNECT_PROJECT_ID;
  if (envValue && typeof envValue === 'string' && envValue.trim()) {
    return envValue.trim();
  }

  // Fallback to window variables for legacy runtime overrides
  if (typeof window !== 'undefined') {
    const runtimeValue = (window as unknown as Record<string, unknown>)?.VITE_WALLETCONNECT_PROJECT_ID;
    if (typeof runtimeValue === 'string' && runtimeValue.trim()) {
      return runtimeValue.trim();
    }
  }

  return undefined;
}

function resolveProjectId(): string {
  const candidate = readRuntimeProjectId();
  if (!candidate || PLACEHOLDER_IDS.has(candidate)) {
    console.info('[AppKit] Using built-in WalletConnect project ID');
    return DEFAULT_PROJECT_ID;
  }

  const normalized = candidate.toLowerCase();
  if (!/^[a-f0-9]{32}$/.test(normalized)) {
    console.warn(`[AppKit] Project ID \"${candidate}\" has an unexpected format. Falling back to default.`);
    return DEFAULT_PROJECT_ID;
  }

  return normalized;
}

const projectId = resolveProjectId();

const metadata = {
  name: 'Token Presale',
  description: 'Purchase tokens in our presale',
  url: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
  icons: [`${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'}/logo.png`]
};

const chains = [mainnet, arbitrum, base, polygon, bsc, avalanche] as const;
const allNetworks = [...chains] as const;

const transports = {
  [mainnet.id]: http(getRpcUrl(
    import.meta.env.VITE_ETHEREUM_RPC_URL,
    'https://rpc.ankr.com/eth'
  )),
  [arbitrum.id]: http(getRpcUrl(
    import.meta.env.VITE_ARBITRUM_RPC_URL,
    'https://rpc.ankr.com/arbitrum'
  )),
  [base.id]: http(getRpcUrl(
    import.meta.env.VITE_BASE_RPC_URL,
    'https://rpc.ankr.com/base'
  )),
  [polygon.id]: http(getRpcUrl(
    import.meta.env.VITE_POLYGON_RPC_URL,
    'https://rpc.ankr.com/polygon'
  )),
  [bsc.id]: http(getRpcUrl(
    import.meta.env.VITE_BNB_RPC_URL,
    'https://rpc.ankr.com/bsc'
  )),
  [avalanche.id]: http(getRpcUrl(
    import.meta.env.VITE_AVALANCHE_RPC_URL,
    'https://rpc.ankr.com/avalanche'
  )),
};

export const wagmiAdapter = new WagmiAdapter({
  networks: [...chains] as any,
  projectId,
  transports
});

// Solana adapter removed - EVM chains only

const noop = () => undefined;

const fallbackAppKit = {
  open: async () => console.warn('[AppKit] open() called before AppKit initialized'),
  close: async () => console.warn('[AppKit] close() called before AppKit initialized'),
  getState: () => ({ open: false }),
  subscribeState: () => noop,
  getRemoteFeatures: () => ({}),
  subscribeRemoteFeatures: () => noop,
  getThemeMode: () => 'light',
  getThemeVariables: () => ({}),
  subscribeTheme: () => noop,
  getEvent: () => undefined,
  subscribeEvents: () => noop,
  switchNetwork: async () => undefined,
  getWalletInfo: () => undefined,
  subscribeWalletInfo: () => noop
} as unknown as AppKit;

let appKit: AppKit = fallbackAppKit;
let initialized = false;

export function clearWalletConnectSessions(): void {
  try {
    // Clear WalletConnect localStorage data
    if (typeof window !== 'undefined' && window.localStorage) {
      const keysToRemove: string[] = [];
      
      // Collect WalletConnect and RPC related keys
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && (
          key.startsWith('wc@2:') || 
          key.startsWith('walletconnect') || 
          key.includes('walletconnect') ||
          key.includes('rpc') ||
          key.includes('alchemy') ||
          key.includes('wagmi') ||
          key.includes('appkit')
        )) {
          keysToRemove.push(key);
        }
      }
      
      // Remove collected keys
      keysToRemove.forEach(key => {
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          console.warn(`Failed to remove localStorage key ${key}:`, error);
        }
      });
      
      console.log(`[AppKit] Cleared ${keysToRemove.length} WalletConnect session(s)`);
    }
  } catch (error) {
    console.warn('[AppKit] Failed to clear WalletConnect sessions:', error);
  }
}

export function initializeAppKit(force = false): AppKit {
  if (initialized && !force) {
    return appKit;
  }

  if (typeof window === 'undefined') {
    getAppKit(fallbackAppKit);
    appKit = fallbackAppKit;
    initialized = false;
    return appKit;
  }

  try {
    // Clear stale WalletConnect sessions if force initializing
    if (force) {
      clearWalletConnectSessions();
    }

    const instance = createAppKit({
      adapters: [wagmiAdapter],
      networks: [...allNetworks],
      projectId,
      metadata,
      themeMode: 'light',
      features: {
        analytics: false,
        email: false,
        socials: false
      },
      defaultNetwork: mainnet
    });

    appKit = instance;
    initialized = true;
    return appKit;
  } catch (error) {
    console.error('[AppKit] Failed to initialize AppKit', error);
    
    // Try clearing sessions and reinitializing once more
    if (!force) {
      console.log('[AppKit] Attempting to clear sessions and reinitialize...');
      clearWalletConnectSessions();
      return initializeAppKit(true);
    }
    
    getAppKit(fallbackAppKit);
    appKit = fallbackAppKit;
    initialized = false;
    return appKit;
  }
}

// Initialize AppKit immediately if in browser environment
if (typeof window !== 'undefined') {
  appKit = initializeAppKit();
}

export { appKit };

export function isNetworkEnabled(network: string): boolean {
  const enableFlags: Record<string, boolean> = {
    Ethereum: import.meta.env.VITE_ENABLE_ETHEREUM === 'true',
    Arbitrum: import.meta.env.VITE_ENABLE_ARBITRUM === 'true',
    Base: import.meta.env.VITE_ENABLE_BASE === 'true',
    Polygon: import.meta.env.VITE_ENABLE_POLYGON === 'true',
    BNB: import.meta.env.VITE_ENABLE_BNB === 'true',
    Avalanche: import.meta.env.VITE_ENABLE_AVALANCHE === 'true',
  };
  
  return enableFlags[network] || false;
}

export function getEnabledChains() {
  return chains.filter(chain => {
    const networkName = chainIdToNetworkName(chain.id);
    return isNetworkEnabled(networkName);
  });
}

function chainIdToNetworkName(chainId: number): string {
  const mapping: Record<number, string> = {
    1: 'Ethereum',
    42161: 'Arbitrum',
    8453: 'Base',
    137: 'Polygon',
    56: 'BNB',
    43114: 'Avalanche',
  };
  
  return mapping[chainId] || '';
}
